<!--
  Settings — Dashboard layout (redesigned 2026-05-25).

  Structure:
    1. Sticky header with title, subtitle, year picker.
    2. Section "🖨️ Stampanti" — one card per printer, with the 4
       role toggles (DDT / Scatola / Corriere / Borderò) inline.
    3. Section "🎯 Obiettivi commerciali" — KPI summary card +
       12 month cards in a 4×3 responsive grid + annual highlight.

  Visual coherence with the Statistics page (palette, accent strip,
  emoji headings, card style).
-->

<div class="settings-page">

  <!-- ─── Sticky header ─── -->
  <header class="settings-header">
    <div class="header-titles">
      <h1 class="settings-title">Impostazioni</h1>
      <p class="settings-subtitle">Configurazione stampanti e obiettivi commerciali</p>
    </div>
  </header>

  <hr class="settings-divider" />

  <!-- ─── SECTION: Stampanti ─── -->
  <h2 class="section-heading">🖨️ Stampanti</h2>
  <p class="section-hint">
    Assegna ogni tipologia di documento alla stampante corrispondente.
    Ogni ruolo (DDT, Scatola, Corriere, Borderò) può essere attivo su
    una sola stampante alla volta.
  </p>

  @if (printers.length === 0) {
    <div class="settings-empty-state">
      <i class="bx bx-printer"></i>
      <p>Nessuna stampante configurata</p>
    </div>
  } @else {
    <section class="printers-grid" aria-label="Stampanti disponibili">
      @for (printer of printers; track printer.printer_id) {
        <article class="printer-card">
          <header class="printer-card-header">
            <i class="bx bxs-printer printer-icon"></i>
            <h3 class="printer-name">{{ printer.printer_desc }}</h3>
          </header>
          <div class="printer-roles">
            @for (role of PRINTER_ROLES; track role.id) {
              <button
                type="button"
                class="role-toggle"
                [class.role-on]="printerHasRole(printer, role.id)"
                (click)="setPrinterAssignment(printer.printer_id, role.id)"
                [title]="(printerHasRole(printer, role.id) ? 'Disattiva' : 'Attiva') + ' ' + role.label"
              >
                <i class="bx" [class]="role.icon"></i>
                <span class="role-label">{{ role.label }}</span>
                <span class="role-indicator" aria-hidden="true"></span>
              </button>
            }
          </div>
        </article>
      }
    </section>
  }

  <!-- ─── SECTION: Obiettivi commerciali ─── -->
  <div class="objectives-heading-row">
    <h2 class="section-heading">🎯 Obiettivi commerciali</h2>
    <div class="year-picker">
      <label for="year">Anno</label>
      <kendo-dropdownlist
        id="year"
        [data]="years"
        [value]="chosenYear"
        (valueChange)="changeYear($event)"
        class="year-dropdown"
      >
        <ng-template let-dataItem>{{ dataItem }}</ng-template>
      </kendo-dropdownlist>
    </div>
  </div>

  <!-- KPI summary card: yearly target + progress bar -->
  @if (isLoadingObjectives) {
    <div class="skeleton skeleton-summary"></div>
    <div class="objectives-grid">
      @for (i of [1,2,3,4,5,6,7,8,9,10,11,12]; track i) {
        <div class="skeleton skeleton-month-card"></div>
      }
    </div>
  } @else {
    <article class="yearly-summary-card">
      <div class="yearly-summary-main">
        <span class="yearly-summary-label">Obiettivo annuale {{ chosenYear }}</span>
        <span class="yearly-summary-value">{{ formatCurrency(totalYearlyAmount) }}</span>
      </div>
      <div class="yearly-summary-progress">
        <div class="progress-track">
          <div
            class="progress-fill"
            [style.width.%]="yearlyProgressPercent">
          </div>
        </div>
        <div class="progress-info">
          <span class="progress-value">
            {{ formatCurrency(completedYearlyAmount) }}
            <span class="progress-total">
              / {{ formatCurrency(totalYearlyAmount) }}
            </span>
          </span>
          <span class="progress-percent">
            {{ yearlyProgressPercent }}% raggiunto
          </span>
        </div>
      </div>
    </article>

    <!-- 12 month cards in 4×3 grid -->
    <section class="objectives-grid" aria-label="Obiettivi mensili">
      @for (month of newObjectivesArray.slice(0, 12); track month.monthly_objective) {
        <article
          class="month-card"
          [class.month-current]="getMonthStatus(month) === 'current'"
          [class.month-saved]="getMonthStatus(month) === 'saved'"
          [class.month-empty]="getMonthStatus(month) === 'empty'"
        >
          <header class="month-card-header">
            <span class="month-name">{{ month.correspondingMonth }}</span>
            @if (getMonthStatus(month) === 'current') {
              <span class="month-badge badge-current">⚡ Mese in corso</span>
            } @else if (getMonthStatus(month) === 'saved') {
              <span class="month-badge badge-saved">✓ Salvato</span>
            } @else {
              <span class="month-badge badge-empty">Da impostare</span>
            }
          </header>

          <div class="month-field">
            <label [attr.for]="'net-' + month.monthly_objective">Obiettivo netto (€)</label>
            <!--
              🚨 FIX v2.11.2 (Q6): rimosso (blur)="transformAmount($event)".
              transformAmount usava currencyPipe per scrivere "€ 10,00" come
              .value dell'input — ma con type="number" il browser RIFIUTA
              stringhe non puramente numeriche → l'input veniva resettato a
              empty/zero al blur → next save inviava net_value=0 → "Salvato!"
              + card resettata a zero. Il currency formatting display rimane
              sul target giornaliero qua sotto (formatCurrency).
            -->
            <input
              [id]="'net-' + month.monthly_objective"
              type="number"
              class="month-input"
              [(ngModel)]="month.net_value"
              (input)="onInputChange($event, month)"
              [placeholder]="month.formattedNetValue || '0,00'"
              min="0"
              step="0.01"
            />
          </div>

          <div class="month-field">
            <label [attr.for]="'days-' + month.monthly_objective">Giorni lavorativi</label>
            <input
              [id]="'days-' + month.monthly_objective"
              type="number"
              class="month-input"
              [(ngModel)]="month.working_days"
              [placeholder]="'0'"
              min="0"
              max="31"
            />
          </div>

          <div class="month-derived">
            <span class="month-derived-label">Target giornaliero</span>
            <span class="month-derived-value">
              {{ month.working_days > 0
                ? formatCurrency(month.net_value / month.working_days)
                : '—' }}
            </span>
          </div>

          <button
            type="button"
            class="month-save-btn"
            [disabled]="month.working_days === 0 || month.net_value === 0"
            (click)="saveObjective(month)"
          >
            <i class="bx bx-save"></i>
            <span>Salva</span>
          </button>
        </article>
      }
    </section>
  }

</div>
